import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { ScoopColumn, ScoopCenter, ScoopText, ScoopButton, ScoopSpinner, useToast } from '../../ui'
import { useInvitations } from '../../../api/hooks/account/useInvitations'
import { useUserInfo } from '../../../api/hooks/user/useUserInfo'
import { ROUTES } from '../../../router/routes'
import styles from './AcceptInvitation.module.scss'

export const AcceptInvitation = () => {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const invitationToken = searchParams.get('token')
    const returnTo = searchParams.get('returnTo') // Check if we should return to onboarding
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated)
    const { user, loadingUser } = useUserInfo()
    const toast = useToast()

    // Use the unified invitations hook
    const {
        invitation,
        validatingInvitation,
        validationError,
        acceptInvitation,
        isAcceptingInvitation,
    } = useInvitations({
        invitationToken,
    })

    // Handle case when user is not authenticated
    useEffect(() => {
        if (!invitationToken) {
            navigate(ROUTES.LOGIN)
            return
        }

        // If user not authenticated, redirect to login immediately (don't wait for validation)
        if (!loadingUser && !isAuthenticated) {
            // Store token in localStorage so it can be used after login
            localStorage.setItem('pendingInvitation', invitationToken)
            navigate(ROUTES.LOGIN)
        }
    }, [invitationToken, isAuthenticated, loadingUser, navigate])

    const handleAcceptInvitation = () => {
        acceptInvitation(
            {
                invitationToken,
            },
            {
                onSuccess: () => {
                    // Clean up localStorage
                    localStorage.removeItem('pendingInvitation')

                    toast.success(`Successfully joined ${invitation.accountName}!`)

                    // Redirect based on returnTo parameter
                    setTimeout(() => {
                        if (returnTo === 'onboarding') {
                            navigate(ROUTES.ONBOARDING)
                        } else {
                            // Reload to update workspace list
                            window.location.href = ROUTES.INSIGHTS
                        }
                    }, 1500)
                },
                onError: (error) => {
                    toast.error(error?.message || 'Failed to accept invitation')
                },
            }
        )
    }

    // Loading state
    if (validatingInvitation || loadingUser) {
        return (
            <ScoopCenter className={styles.container}>
                <ScoopColumn className={styles.card}>
                    <ScoopSpinner size={60} />
                    <ScoopText className={styles.loadingText}>Validating invitation...</ScoopText>
                </ScoopColumn>
            </ScoopCenter>
        )
    }

    // Handle error states based on error codes from FRONTEND_INVITATION_GUIDE.md
    if (validationError) {
        const errorCode = validationError?.code
        let errorTitle = 'Invalid Invitation'
        let errorMessage = validationError?.message || 'This invitation is invalid or has expired'
        let showAccountInfo = false

        switch (errorCode) {
            case 'INVITATION_EXPIRED':
                errorTitle = 'Invitation Expired'
                errorMessage =
                    'This invitation has expired. Please request a new invitation from your administrator.'
                break
            case 'ALREADY_ACCEPTED':
                errorTitle = 'Already Accepted'
                errorMessage = `You've already joined ${validationError?.body?.accountName || 'this account'}!`
                showAccountInfo = true
                break
            case 'INVITATION_CANCELLED':
                errorTitle = 'Invitation Cancelled'
                errorMessage = 'This invitation has been cancelled by an account administrator.'
                break
            case 'INVITATION_NOT_FOUND':
                errorTitle = 'Invitation Not Found'
                errorMessage = 'This invitation link is invalid or does not exist.'
                break
            default:
                break
        }

        return (
            <ScoopCenter className={styles.container}>
                <ScoopColumn className={styles.card}>
                    <ScoopText className={styles.errorIcon}>⚠️</ScoopText>
                    <ScoopText className={styles.title}>{errorTitle}</ScoopText>
                    <ScoopText className={styles.description}>{errorMessage}</ScoopText>
                    {showAccountInfo && validationError?.body?.accountName && (
                        <ScoopText className={styles.accountName}>
                            {validationError.body.accountName}
                        </ScoopText>
                    )}
                    <ScoopColumn className={styles.actions}>
                        <ScoopButton
                            onClick={() => navigate(ROUTES.CANVAS_DASHBOARD)}
                            variant="default"
                            size="fullWidth"
                        >
                            Go to Dashboard
                        </ScoopButton>
                        <ScoopButton
                            onClick={() => navigate(ROUTES.LOGIN)}
                            variant="outline"
                            size="fullWidth"
                        >
                            Go to Login
                        </ScoopButton>
                    </ScoopColumn>
                </ScoopColumn>
            </ScoopCenter>
        )
    }

    // Invalid invitation (valid field is false but no error thrown)
    if (!invitation?.valid) {
        return (
            <ScoopCenter className={styles.container}>
                <ScoopColumn className={styles.card}>
                    <ScoopText className={styles.errorIcon}>⚠️</ScoopText>
                    <ScoopText className={styles.title}>Invalid Invitation</ScoopText>
                    <ScoopText className={styles.description}>
                        This invitation is no longer valid. Please contact your administrator.
                    </ScoopText>
                    <ScoopColumn className={styles.actions}>
                        <ScoopButton
                            onClick={() => navigate(ROUTES.LOGIN)}
                            variant="outline"
                            size="fullWidth"
                        >
                            Go to Login
                        </ScoopButton>
                    </ScoopColumn>
                </ScoopColumn>
            </ScoopCenter>
        )
    }

    // User not authenticated - they will be redirected to login by the useEffect above
    if (!isAuthenticated) {
        return (
            <ScoopCenter className={styles.container}>
                <ScoopColumn className={styles.card}>
                    <ScoopSpinner size={60} />
                    <ScoopText className={styles.loadingText}>Redirecting to login...</ScoopText>
                </ScoopColumn>
            </ScoopCenter>
        )
    }

    // User authenticated - show accept screen
    return (
        <ScoopCenter className={styles.container}>
            <ScoopColumn className={styles.card}>
                <ScoopText className={styles.icon}>🎉</ScoopText>
                <ScoopText className={styles.title}>Account Invitation</ScoopText>
                <ScoopText className={styles.description}>
                    <strong>{invitation.inviterName}</strong> invited you to join
                </ScoopText>
                <ScoopText className={styles.accountName}>{invitation.accountName}</ScoopText>
                <ScoopColumn className={styles.details}>
                    <div className={styles.detailRow}>
                        <ScoopText className={styles.detailLabel}>Your account role:</ScoopText>
                        <ScoopText className={styles.detailValue}>{invitation.role}</ScoopText>
                    </div>
                    <div className={styles.detailRow}>
                        <ScoopText className={styles.detailLabel}>Invited by:</ScoopText>
                        <ScoopText className={styles.detailValue}>
                            {invitation.inviterEmail}
                        </ScoopText>
                    </div>
                </ScoopColumn>
                <ScoopColumn className={styles.actions}>
                    <ScoopButton
                        onClick={handleAcceptInvitation}
                        disabled={isAcceptingInvitation}
                        loading={isAcceptingInvitation}
                        variant="default"
                        size="fullWidth"
                    >
                        Accept Invitation
                    </ScoopButton>
                    <ScoopButton
                        onClick={() =>
                            navigate(
                                returnTo === 'onboarding' ? ROUTES.ONBOARDING : ROUTES.INSIGHTS
                            )
                        }
                        variant="outline"
                        size="fullWidth"
                    >
                        Maybe Later
                    </ScoopButton>
                </ScoopColumn>
            </ScoopColumn>
        </ScoopCenter>
    )
}
